import type { FleetActivitySummary, FleetRun, FleetSummary } from './claude-fleet.types'
import { BOOKKEEPING_ACTIVITY, isRunLive } from './claude-fleet.utils'
import { runGradedAs, type FleetRunGraded } from './claude-fleet.grades'
import { formatCurrency } from '@/utils/dateUtils'

/**
 * How the Runs tab reads a run record. The fleet filters the runs list by the same outcomes and
 * triggers (tattle-claude-fleet `src/fleet-runs.ts`), so the two must agree on what each one means.
 */

export const FLEET_RUN_OUTCOMES = ['running', 'ok', 'skipped', 'stopped', 'budget', 'error'] as const
export type FleetRunOutcome = (typeof FLEET_RUN_OUTCOMES)[number]

export const FLEET_RUN_TRIGGERS = ['schedule', 'adx', 'claude', 'pr-watch', 'queue', 'lane', 'manual'] as const
export type FleetRunTrigger = (typeof FLEET_RUN_TRIGGERS)[number]

/**
 * A trigger a run record carries that the fleet's runs filter does not take: a laptop records `inbox` on the runs
 * it starts from its own queue (workspace `bin/run_outcomes.py`), so the page names it but never filters by it.
 */
export type FleetRunTriggerKind = FleetRunTrigger | 'inbox'

export const FLEET_RUN_SINCE = ['1h', '24h', '7d', '30d'] as const
export type FleetRunSince = (typeof FLEET_RUN_SINCE)[number]

export interface FleetRunFilters {
  outcome: FleetRunOutcome | null
  trigger: FleetRunTrigger | null
  since: FleetRunSince | null
  /** A ticket key or `<repo>#<id>` PR, as parseRunSearch normalises it. */
  search: string | null
  /** Only the viewer's runs: started by them, run on their laptop or DMing them. The page filters these itself. */
  mine?: boolean
  /** Only the runs one laptop runner did, by its machine name. */
  machine?: string | null
  /** Only runs people graded 👍 or 👎, or graded at all. */
  graded?: FleetRunGraded | null
  /** List the bookkeeping runs too; otherwise only their failures are listed, under one folded line. */
  bookkeeping?: boolean
}

export interface FleetRunOutcomeView {
  outcome: FleetRunOutcome
  /** What follows the status word on the RUN_STATUS line, less a leading tick id, e.g. `skipped-idle`. */
  reason: string
}

export interface FleetRunTriggerView {
  trigger: FleetRunTriggerKind | null
  label: string
  /** The raw trigger, for the chip's hover. */
  title: string
}

export interface FleetRunLink {
  label: string
  url: string
  /** The PR's title or the ticket's summary, when the fleet sent it. */
  title?: string
}

export const FLEET_OUTCOME_LABELS: Record<FleetRunOutcome, string> = {
  running: 'Running',
  ok: 'OK',
  skipped: 'Skipped',
  stopped: 'Stopped',
  budget: 'Budget',
  error: 'Error'
}

export const FLEET_TRIGGER_LABELS: Record<FleetRunTriggerKind, string> = {
  schedule: 'Schedule',
  adx: 'ADX',
  claude: 'Claude',
  'pr-watch': 'PR watch',
  queue: 'Queue',
  lane: 'Lane',
  manual: 'Manual',
  inbox: 'Own queue'
}

export const FLEET_SINCE_LABELS: Record<FleetRunSince, string> = {
  '1h': 'Last hour',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days'
}

const SINCE_MS: Record<FleetRunSince, number> = {
  '1h': 3_600_000,
  '24h': 86_400_000,
  '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000
}

const JIRA_BROWSE_URL = 'https://tattle.atlassian.net/browse/'
const BITBUCKET_WORKSPACE_URL = 'https://bitbucket.org/tattledevs/'
const JIRA_KEY = /^[A-Z][A-Z0-9]{1,9}-\d+$/
/** A run's item names its PR as `<repo>#<id>` and its Jira mention as `jira:<KEY>#<comment>`. */
const PR_ITEM = /^([a-z][a-z0-9._-]*)#(\d+)$/
const PR_URL = /bitbucket\.org\/[^/\s]+\/([A-Za-z0-9._-]+)\/pull-requests\/(\d+)/
const JIRA_ITEM = /^(?:jira:)?([A-Z][A-Z0-9]{1,9}-\d+)(?:#\d+)?$/
/** A release ticket's own production test or try again, `13.21.0#only-TE-1`, as the fleet's runs search reads it. */
const RELEASE_TICKET_ITEM = /^[^#]+#(?:only|retry)-([A-Z][A-Z0-9]{1,9}-\d+)$/
/** Each live log line starts with the runner's `[run <id>] ` prefix. */
const RUN_LOG_PREFIX = /^\[run [^\]]+\]\s*/

function statusParts(runStatus: string | undefined): { word: string; reason: string } {
  const body = (runStatus ?? '').replace(/^RUN_STATUS=/, '')
  const colon = body.indexOf(':')
  if (colon === -1) return { word: body.toLowerCase(), reason: '' }
  return { word: body.slice(0, colon).toLowerCase(), reason: body.slice(colon + 1) }
}

/** A run that stood down because its loop is paused (`error:paused:qa-verify`, `error:loop-paused:…`) did nothing on purpose. */
const PAUSED_REASON = /^(loop-)?paused(:|$)/i

function outcomeFor(run: FleetRun, word: string): FleetRunOutcome {
  const status = run.runStatus ?? ''
  if (isRunLive(run.state)) return 'running'
  if (run.state === 'stopped' || word === 'stopped') return 'stopped'
  if (run.budgetHit || /budget/i.test(status)) return 'budget'
  if (PAUSED_REASON.test(statusParts(status).reason)) return 'skipped'
  if (run.state === 'error' || word === 'error' || word.startsWith('fail')) return 'error'
  if (run.state === 'skipped' || /skip/i.test(status)) return 'skipped'
  return 'ok'
}

/**
 * A loop tick often starts its reason with its own tick id (`lpe-loop-9-2026-10-10T2015Z:review-only`),
 * which says nothing the row does not; the id is dropped from what the chip shows, never from the hover.
 */
function withoutTickId(reason: string, activity: string): string {
  const segments = reason.split(':')
  const [first] = segments
  const isTickId = segments.length > 1 && (first!.startsWith(activity) || first!.startsWith('lane'))
  return isTickId ? segments.slice(1).join(':') : reason
}

/** A run id, tick id or date the runner puts in a RUN_STATUS reason: `2026-10-11-adx867`, `fq-20261011-160507`. */
const ID_SEGMENT = /^(\d{4}-\d{2}-\d{2}|[a-z]{1,8}-\d{8}(-[a-z0-9]+)?$)/

const PLAIN_REASONS: [RegExp, (match: RegExpExecArray) => string][] = [
  [/^skipped-idle$/, () => 'Nothing to do'],
  [PAUSED_REASON, () => 'Stood down: its loop is paused'],
  [/^review-only$/, () => 'Looked after its open PRs only'],
  [/^halted-at-stage-(\d+)$/, match => `Stopped at stage ${match[1]} for a decision`],
  [/^dispatched-([A-Z][A-Z0-9]+-\d+)$/, match => `Started delivering ${match[1]}`],
  [/^no-status-line$/, () => 'Ended without saying how it went'],
  [/^nothing-to-fix(:|$)/, () => 'Nothing to fix: no open review findings'],
  [/^no-prs(:|$)/, () => 'No open PRs to work on'],
  [/^not-open(:|$)/, () => 'The PR was already merged or declined'],
  [/^already-reviewed(:|$)/, () => 'Already reviewed at this commit'],
  [/^lease-held(:|$)/, () => 'Another session is already working on this PR'],
  [/^fix-loop-owned(:|$)/, () => 'The PR\'s own watch loop answers its threads'],
  [/^not-yours(:|$)/, () => 'A Claude tooling PR that is not yours'],
  [/^not-opted-in(:|$)/, () => 'Fixes are off for your PRs in Claude on my PRs'],
  [/^level-unreadable(:|$)/, () => 'Could not read your Claude on my PRs setting'],
  [/^mixed(:|$)/, () => 'Several PRs skipped for different reasons: open the run for each']
]

function reasonCode(reason: string): string {
  return reason
    .split(':')
    .filter(segment => segment && !ID_SEGMENT.test(segment))
    .join(':')
}

/** The words for a reason code the page knows, or null for one it does not. */
function knownRunReason(reason: string): string | null {
  const code = reasonCode(reason)
  for (const [pattern, words] of PLAIN_REASONS) {
    const match = pattern.exec(code)
    if (match) return words(match)
  }
  return null
}

/** A RUN_STATUS reason in words, without the ids and tick names it carries for the runner's own logs. */
export function plainRunReason(reason: string, outcome: FleetRunOutcome): string {
  const code = reasonCode(reason)
  if (!code || code.toLowerCase() === outcome) return ''
  const known = knownRunReason(code)
  if (known) return known
  const spaced = code.replace(/[-_:]+/g, ' ').trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * How a run ended, as the list, the Running now card and the drawer all show it: the outcome, its
 * label, and one plain reason. A code the page has words for wins, then the fleet's own sentence
 * (it knows budgets and hand-backs), then the code itself in words.
 */
export function describeRunOutcome(run: FleetRun): { outcome: FleetRunOutcome; label: string; reason: string } {
  const { outcome, reason } = runOutcome(run)
  const words = knownRunReason(reason) ?? (run.reason?.text?.trim() || plainRunReason(reason, outcome))
  return { outcome, label: FLEET_OUTCOME_LABELS[outcome], reason: words }
}

/** The run's outcome from its state, budget and `RUN_STATUS=<word>:<reason>` line. */
export function runOutcome(run: FleetRun): FleetRunOutcomeView {
  const { word, reason } = statusParts(run.runStatus)
  return { outcome: outcomeFor(run, word), reason: withoutTickId(reason, run.activity) }
}

const OUTCOME_COLORS: Record<FleetRunOutcome, 'blue' | 'green' | 'gray' | 'yellow' | 'red'> = {
  running: 'blue',
  ok: 'green',
  skipped: 'gray',
  stopped: 'gray',
  budget: 'yellow',
  error: 'red'
}

export function outcomeColor(outcome: FleetRunOutcome): 'blue' | 'green' | 'gray' | 'yellow' | 'red' {
  return OUTCOME_COLORS[outcome]
}

export function triggerOf(trigger: string | undefined): FleetRunTriggerKind | null {
  const value = trigger ?? ''
  if (value.startsWith('adx:')) return 'adx'
  if (value.startsWith('mcp:')) return 'claude'
  if (value.startsWith('lane:')) return 'lane'
  if (value === 'inbox') return 'inbox'
  return oneOf(value, FLEET_RUN_TRIGGERS)
}

/** The person a run started from ADX or by Claude was started for: the email's local part. */
function personOf(trigger: string): string {
  const who = trigger.slice(trigger.indexOf(':') + 1)
  return who.split('@')[0] || who
}

/** A short chip for who or what started the run: `adx:pat@gettattle.com` reads "ADX · pat". */
export function describeTrigger(trigger: string | undefined): FleetRunTriggerView {
  const raw = trigger ?? ''
  const kind = triggerOf(raw)
  if (!kind) return { trigger: null, label: raw || '—', title: raw }
  const label = FLEET_TRIGGER_LABELS[kind]
  if (kind === 'adx' || kind === 'claude') return { trigger: kind, label: `${label} · ${personOf(raw)}`, title: raw }
  return { trigger: kind, label, title: raw }
}

export function ticketLink(key: string): FleetRunLink {
  return { label: key, url: `${JIRA_BROWSE_URL}${key}` }
}

export function pullRequestLink(ref: string): FleetRunLink | null {
  const match = PR_ITEM.exec(ref)
  if (!match) return null
  return { label: ref, url: `${BITBUCKET_WORKSPACE_URL}${match[1]}/pull-requests/${match[2]}` }
}

/**
 * The Jira tickets and pull requests a run worked on: its item (what it was started for) and the ones
 * the runner recorded from the run's own outcome. Records written before the runner kept them have
 * only the item.
 */
export function runTouched(run: FleetRun): { tickets: FleetRunLink[]; pullRequests: FleetRunLink[] } {
  const tickets = new Set<string>(run.tickets?.filter(key => JIRA_KEY.test(key)))
  const pullRequests = new Set<string>(run.pullRequests)
  const item = run.item ?? ''
  const jiraItem = JIRA_ITEM.exec(item)
  if (jiraItem) tickets.add(jiraItem[1]!)
  if (PR_ITEM.test(item)) pullRequests.add(item)
  const titled = (link: FleetRunLink): FleetRunLink => {
    const title = run.titles?.[link.label]
    return title ? { ...link, title } : link
  }
  return {
    tickets: [...tickets].map(ticketLink).map(titled),
    pullRequests: [...pullRequests]
      .map(pullRequestLink)
      .filter((link): link is FleetRunLink => link !== null)
      .map(titled)
  }
}

/**
 * What a runs search names, normalised as the fleet reads `q`: a Jira key upper-cased, a `<repo>#<id>`
 * PR lower-cased, or a Bitbucket PR URL read as `<repo>#<id>`. Null for anything else, which the fleet
 * would refuse, so the page asks for a key instead of fetching.
 */
export function parseRunSearch(text: string | null | undefined): string | null {
  const value = (text ?? '').trim()
  if (!value) return null
  const url = PR_URL.exec(value)
  if (url) return `${url[1]!.toLowerCase()}#${url[2]}`
  if (JIRA_KEY.test(value.toUpperCase())) return value.toUpperCase()
  const pr = value.toLowerCase()
  return PR_ITEM.test(pr) ? pr : null
}

/** Whether the run worked on the searched ticket or PR: its item, or the ones it recorded. */
export function runMatchesSearch(run: FleetRun, search: string): boolean {
  const item = run.item ?? ''
  if (item === search || JIRA_ITEM.exec(item)?.[1] === search || RELEASE_TICKET_ITEM.exec(item)?.[1] === search) return true
  return Boolean(run.tickets?.includes(search) || run.pullRequests?.includes(search))
}

/** Runs that cost at least this many times their activity's median are flagged. */
export const UNUSUAL_COST_RATIO = 3
/** A median taken over fewer finished runs than this says too little to flag against. */
export const UNUSUAL_COST_MIN_SAMPLES = 5

/** How many times its activity's median the run cost, when that is unusually high; else null. */
export function unusualCostRatio(run: FleetRun, activity: FleetActivitySummary | undefined): number | null {
  const median = activity?.medianCostUsd
  if (typeof run.costUsd !== 'number' || typeof median !== 'number' || median <= 0) return null
  if ((activity?.medianOf ?? 0) < UNUSUAL_COST_MIN_SAMPLES) return null
  const ratio = run.costUsd / median
  return ratio >= UNUSUAL_COST_RATIO ? ratio : null
}

/** A run's unusual cost in words ("3.4× this activity's median of $0.42"), or null when it is not unusual. */
export function unusualCostText(run: FleetRun, activity: FleetActivitySummary | undefined): string | null {
  const ratio = unusualCostRatio(run, activity)
  if (ratio === null) return null
  return `${ratio.toFixed(1)}× this activity's median of ${formatCurrency(activity!.medianCostUsd!)}`
}

/** The ticket or PR the outcome scorer judged the run by, else the run's own item when it names one. */
export function scoreSubjectLink(run: FleetRun): FleetRunLink | null {
  const subject = run.score?.subject
  if (subject?.url) return { label: subject.ref, url: subject.url }
  const ref = subject?.ref ?? run.item ?? ''
  const jira = JIRA_ITEM.exec(ref)
  if (jira) return ticketLink(jira[1]!)
  return pullRequestLink(ref)
}

/** The start of a "since" window as an ISO time. */
export function sinceTime(since: FleetRunSince, now: number): string {
  return new Date(now - SINCE_MS[since]).toISOString()
}

/**
 * Whether a run passes the filters. The fleet applies them too; this keeps the list right against an
 * older fleet service that predates them and ignores the parameters.
 */
export function runMatchesFilters(run: FleetRun, filters: FleetRunFilters, now: number, viewerEmail?: string | null): boolean {
  if (filters.outcome && runOutcome(run).outcome !== filters.outcome) return false
  if (filters.trigger && triggerOf(run.trigger) !== filters.trigger) return false
  if (filters.since && run.startedAt < sinceTime(filters.since, now)) return false
  if (filters.search && !runMatchesSearch(run, filters.search)) return false
  if (filters.mine && (!viewerEmail || !isRunMine(run, viewerEmail))) return false
  // The fleet filters by runner too; checking here keeps the list right on a fleet service that ignores it.
  if (filters.machine && run.runnerMachine !== filters.machine) return false
  if (filters.graded && !runGradedAs(run, filters.graded)) return false
  return true
}

/** Outcomes where the run did not finish its work: a person may want them explained, and the list never hides them. */
const UNFINISHED_OUTCOMES: FleetRunOutcome[] = ['error', 'budget', 'stopped']

/**
 * A bookkeeping run the list leaves out while bookkeeping is hidden. The fleet leaves them out too, but
 * still lists the failed ones; checking here keeps the list right on a fleet service that ignores `hide`.
 */
export function isFoldedBookkeepingRun(run: FleetRun): boolean {
  return run.activity === BOOKKEEPING_ACTIVITY && !UNFINISHED_OUTCOMES.includes(runOutcome(run).outcome)
}

/** Reasons a skipped run gives when it found nothing to do: no findings, no PRs, already reviewed, or idle. */
const NOTHING_TO_DO = /^(nothing-to-fix|skipped-idle|no-prs|already-reviewed|not-open)(:|$)/

/**
 * A run that started, found nothing to do and ended. Most runs are these, and none needs anyone, so the
 * list hides them until the viewer asks with Show skipped; a skip for any other reason stays in view.
 */
export function isNothingToDoRun(run: FleetRun): boolean {
  const { outcome, reason } = runOutcome(run)
  return outcome === 'skipped' && NOTHING_TO_DO.test(reasonCode(reason))
}

/**
 * The folded line that stands for the hidden bookkeeping runs, e.g. `Bookkeeping · 288 runs today · all OK
 * this month`, from the summary's month totals and its New York days (the last one is today).
 */
export function bookkeepingFoldText(summary: Pick<FleetSummary, 'byActivity' | 'daily'> | null, now: Date): string {
  const month = summary?.byActivity[BOOKKEEPING_ACTIVITY]
  if (!month) return 'Bookkeeping runs are hidden'
  const parts = ['Bookkeeping', bookkeepingRunsText(summary, month, now)]
  parts.push(month.failed ? `${plural(month.failed, 'failed run')} this month` : 'all OK this month')
  return parts.join(' · ')
}

function bookkeepingRunsText(summary: Pick<FleetSummary, 'daily'>, month: FleetActivitySummary, now: Date): string {
  const days = summary.daily?.[BOOKKEEPING_ACTIVITY]
  if (!days) return `${plural(month.runs, 'run')} this month`
  const last = days[days.length - 1]
  const today = last?.day === newYorkDay(now) ? last.runs : 0
  return `${plural(today, 'run')} today`
}

function newYorkDay(now: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
}

export function plural(count: number, noun: string): string {
  return `${count.toLocaleString('en-US')} ${noun}${count === 1 ? '' : 's'}`
}

/**
 * A run this person started from the page or through Claude, or that ran on their laptop. A run that only
 * DMs them, such as a release owner's release-verify DM, is not theirs.
 */
export function isRunMine(run: FleetRun, email: string): boolean {
  const me = email.toLowerCase()
  const startedBy = run.trigger?.split(':')[1]?.toLowerCase()
  return startedBy === me || run.runnerOwner?.toLowerCase() === me
}

/**
 * The last 24 hours' failed runs, read from the runs list already loaded when that list reaches back
 * past 24 hours unfiltered; null when it does not, so the caller asks the fleet for them instead.
 */
export function recentFailuresFromPage(
  runs: FleetRun[],
  page: { isFirstUnfilteredPage: boolean; hasOlder: boolean },
  now: number
): FleetRun[] | null {
  if (!page.isFirstUnfilteredPage) return null
  const since = sinceTime('24h', now)
  const oldest = runs.reduce<string | null>((min, run) => (min === null || run.startedAt < min ? run.startedAt : min), null)
  const coversDay = !page.hasOlder || (oldest !== null && oldest < since)
  if (!coversDay) return null
  return runs.filter(run => run.startedAt >= since && runOutcome(run).outcome === 'error')
}

/** The newest step of a running run's live log, without the runner's prefix. */
export function latestStep(tail: string[] | undefined): string | null {
  const lines = (tail ?? []).map(line => line.replace(RUN_LOG_PREFIX, '').trim()).filter(Boolean)
  return lines.length ? lines[lines.length - 1]! : null
}

/** `value` when it is one of `allowed`, else null: for filter values read from a URL or a dropdown. */
export function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return (allowed as readonly unknown[]).includes(value) ? (value as T) : null
}

/** How long the page keeps re-reading a run whose diagnosis is still being written. */
export const FLEET_DIAGNOSIS_WAIT_MS = 120_000

/** Whether "Explain this failure" applies: a finished run that ended badly, or one already explained. */
export function isRunExplainable(run: FleetRun): boolean {
  if (isRunLive(run.state)) return false
  return Boolean(run.diagnosis) || UNFINISHED_OUTCOMES.includes(runOutcome(run).outcome)
}

/** A diagnosis still being written, asked for recently enough that waiting on it is worthwhile. */
export function isDiagnosisPending(run: FleetRun, now: number): boolean {
  const diagnosis = run.diagnosis
  if (diagnosis?.state !== 'pending' || !diagnosis.requestedAt) return false
  return now - Date.parse(diagnosis.requestedAt) < FLEET_DIAGNOSIS_WAIT_MS
}
